/**
 * VendorFormPage — G-2. 자재파트너 등록 / 편집
 *
 * - /vendors/new             → 등록 모드
 * - /vendors/:vendorId/edit  → 편집 모드 (location.state.vendor 또는 목록 조회 fallback)
 *
 * 자재파트너는 항상 사업자(개인 구분 없음). 중복판정 = 사업자번호(vendorBizNo).
 */
import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Input, MultiSelect, Segmented, Textarea } from '@indipia/ui-core';
import { SIGUNGU_LIST, COMM_METHOD_OPTIONS, COMM_METHOD_HELP_MATERIAL } from '@indipia/shared';
import { fetchVendors, createVendor, updateVendor, checkVendorExists, fetchOrderSettingVendorWorkTypes } from '../api/vendors';
import type { VendorItem } from '../api/vendors';
import { fetchWorkTypes, fetchCommonCodes } from '../api/work-types';

// 배송지역 시군구 옵션(전체) + 코드→명 조회 — @indipia/shared 정적 데이터
const SIGUNGU_OPTIONS = SIGUNGU_LIST.map((s) => ({ value: s.code, label: s.name }));
const SIGUNGU_NAME_BY_CODE = new Map(SIGUNGU_LIST.map((s) => [s.code, s.name]));

// ── 타입 ──────────────────────────────────────────────────────────────────────

interface FormEntry {
  vendorName: string;      // 상호명
  bizNo: string;           // 사업자번호
  repName: string;         // 대표자명
  contactName: string;     // 담당자명
  phone: string;           // 담당자 연락처
  email: string;
  dealStatus: string;      // 거래 상태 (공통코드 E01_PARTNER_DEAL_STATUS: ACTIVE/SUSPENDED)
  commYn: boolean;         // 연락 방식 (true=알림톡 발송-회신 · false=결과만 직접등록, 기본 true)
  workCodes: string[];     // 거래품목 공종 코드(다중)
  dealItem: string;        // 세부취급품목(자유텍스트)
  sigunguCodes: string[];  // 배송지역 시군구 코드(다중)
  memo: string;
}

const EMPTY_FORM: FormEntry = {
  vendorName: '',
  bizNo: '',
  repName: '',
  contactName: '',
  phone: '',
  email: '',
  dealStatus: 'ACTIVE',    // 거래 상태 기본값 = 거래중
  commYn: true,            // 연락 방식 기본값 = 알림톡 발송-회신
  workCodes: [],
  dealItem: '',
  sigunguCodes: [],
  memo: '',
};

type BizNoCheckStatus = 'idle' | 'checking' | 'ok' | 'duplicate';

// ── 헬퍼 ──────────────────────────────────────────────────────────────────────

function formatBizNo(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    const d = digits.slice(0, 10);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: FormEntry): string | null {
  if (!form.vendorName.trim()) return '상호명을 입력해주세요.';
  if (!form.bizNo.trim()) return '사업자번호를 입력해주세요.';
  if (!form.repName.trim()) return '대표자명을 입력해주세요.';
  if (!form.contactName.trim()) return '담당자명을 입력해주세요.';
  if (!form.phone.trim()) return '담당자 연락처를 입력해주세요.';
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) return '이메일 형식이 올바르지 않습니다.';
  if (form.workCodes.length === 0) return '거래 품목을 선택해주세요.';
  return null;
}

// forUpdate=true(수정): 서버 계약상 null=미변경·""=비움 → 비운 선택 입력은 빈 문자열로 전송. 등록은 기존대로 null.
function toPayload(form: FormEntry, forUpdate = false) {
  const opt = (v: string) => (forUpdate ? v.trim() : (v.trim() || null));
  return {
    vendorTypeCode: 'MATERIAL' as const,
    vendorName: form.vendorName.trim(),
    vendorBizNo: form.bizNo.trim() || null,
    vendorRepName: form.repName.trim() || null,
    contactName: form.contactName.trim() || null,
    vendorPhone: form.phone.trim(),
    vendorEmail: opt(form.email),
    dealStatus: form.dealStatus || null,
    commYn: form.commYn ? 'Y' : 'N',
    workTypes: form.workCodes,
    dealItem: opt(form.dealItem),
    sigungus: form.sigunguCodes.map((c) => ({ sigunguCode: c, sigunguName: SIGUNGU_NAME_BY_CODE.get(c) ?? c })),
    vendorMemo: opt(form.memo),
  };
}

function vendorToForm(v: VendorItem): FormEntry {
  return {
    vendorName: v.vendorName,
    bizNo: v.vendorBizNo ?? '',
    repName: v.vendorRepName ?? '',
    contactName: v.contactName ?? '',
    phone: v.vendorPhone,
    email: v.vendorEmail ?? '',
    dealStatus: v.dealStatus ?? 'ACTIVE',
    commYn: v.commYn !== 'N',
    workCodes: v.workTypes ?? [],
    dealItem: v.dealItem ?? '',
    sigunguCodes: (v.sigungus ?? []).map((s) => s.sigunguCode),
    memo: v.vendorMemo ?? '',
  };
}

// 서버 에러 메시지(예: 중복) 추출 — 없으면 null
function serverMsg(e: unknown): string | null {
  const m = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return m || null;
}

const INPUT_CLS = 'placeholder:text-text-muted focus:border-group focus:ring-0';

// 라벨-입력 한 줄 정렬용 2열 그리드(라벨 고정폭 + 입력). 모든 행 동일 구조로 좌측 정렬 일치.
const ROW_GRID: CSSProperties = { display: 'grid', gridTemplateColumns: '6rem 1fr', alignItems: 'center', columnGap: '12px' };

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────

function AddMoreConfirmModal({
  vendorName,
  onConfirm,
  onCancel,
  isPending,
}: {
  vendorName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      style={{ background: 'var(--overlay-scrim)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-surface-raised p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendorSaveConfirmTitle"
      >
        <p id="vendorSaveConfirmTitle" className="text-sm font-bold text-text">현재 입력 내용을 저장하시겠습니까?</p>
        <p className="mt-1.5 text-xs text-text-muted">
          <strong>{vendorName}</strong> 거래처 정보를 저장하고 다음 거래처를 추가합니다.
        </p>
        <div className="mt-5 flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            className="flex-1"
          >
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            disabled={isPending}
            loading={isPending}
            className="flex-1"
          >
            {isPending ? '저장 중…' : '저장 후 추가'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// 거래정지 시 주거래처 지정 해제 확인 모달
function SuspendReleaseConfirmModal({
  workNames,
  onConfirm,
  onCancel,
  isPending,
}: {
  workNames: string[];
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      style={{ background: 'var(--overlay-scrim)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-surface-raised p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendorSuspendConfirmTitle"
      >
        <p id="vendorSuspendConfirmTitle" className="text-sm font-bold text-text">거래정지 전 확인해주세요</p>
        <p className="mt-1.5 text-xs text-text-muted">
          이 자재파트너는 <strong>[{workNames.join('·')}]</strong> 공종의 주거래처로 등록되어 있어요.
          거래정지하면 해당 공종의 주거래처에서 해제됩니다. 계속하시겠습니까?
        </p>
        <div className="mt-5 flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            className="flex-1"
          >
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            disabled={isPending}
            loading={isPending}
            className="flex-1"
          >
            {isPending ? '처리 중…' : '거래정지'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function VendorFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { vendorId } = useParams<{ vendorId: string }>();
  const location = useLocation();

  const { data: workTypes = [] } = useQuery({
    queryKey: ['work-types'],
    queryFn: fetchWorkTypes,
  });

  // 거래 상태 옵션 — 공통코드 E01_PARTNER_DEAL_STATUS (ACTIVE/SUSPENDED) · 시공파트너와 공용
  const { data: dealStatusCodes = [] } = useQuery({
    queryKey: ['common-codes', 'E01_PARTNER_DEAL_STATUS'],
    queryFn: () => fetchCommonCodes('E01_PARTNER_DEAL_STATUS'),
  });
  const dealStatusOptions = useMemo(
    () => dealStatusCodes.map((c) => ({ value: c.codeValue, label: c.codeName })),
    [dealStatusCodes],
  );

  const isEdit = !!vendorId;
  const stateVendor = (location.state as { vendor?: VendorItem } | null)?.vendor;

  const { data: vendorList } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => fetchVendors(),
    enabled: isEdit && !stateVendor,
  });
  const editVendor = stateVendor ?? vendorList?.find((v) => v.vendorId === Number(vendorId));

  const [form, setForm] = useState<FormEntry>(() =>
    isEdit && editVendor ? vendorToForm(editVendor) : EMPTY_FORM,
  );
  const [savedSummaries, setSavedSummaries] = useState<{ vendorName: string; phone: string }[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  // 거래정지 저장 시 주거래처 지정 해제 확인
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [suspendWorkCodes, setSuspendWorkCodes] = useState<string[]>([]);
  const [suspendChecking, setSuspendChecking] = useState(false);

  // 사업자번호 실시간 중복 확인(디바운스)
  const [dupStatus, setDupStatus] = useState<BizNoCheckStatus>('idle');
  useEffect(() => {
    const digits = form.bizNo.replace(/\D/g, '');
    if (digits.length < 10) { setDupStatus('idle'); return; }
    setDupStatus('checking');
    const t = setTimeout(() => {
      checkVendorExists(form.bizNo, isEdit ? Number(vendorId) : undefined)
        .then((exists) => setDupStatus(exists ? 'duplicate' : 'ok'))
        .catch(() => setDupStatus('idle'));
    }, 400);
    return () => clearTimeout(t);
  }, [form.bizNo, isEdit, vendorId]);

  // 편집 모드: 데이터 로드 후 form 동기화
  const [formInitialized, setFormInitialized] = useState(!!stateVendor || !isEdit);
  if (isEdit && editVendor && !formInitialized) {
    setForm(vendorToForm(editVendor));
    setFormInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (vars: { payload: ReturnType<typeof toPayload>; release?: boolean }): Promise<void> => {
      if (isEdit) {
        await updateVendor(Number(vendorId), { action: 'update', ...vars.payload });
      } else {
        await createVendor(vars.payload);
      }
      // 주거래처 지정 해제는 서버(sv-member → sv-schedule)가 거래중지·삭제 저장 커밋 후 처리한다.
    },
    onSuccess: (_, vars) => {
      // 거래정지 확인 모달을 거쳐 저장한 경우에만 안내 문구 표시(API 호출 없음)
      if (vars.release) toast.success('주거래처 지정을 해제했습니다');
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      navigate('/vendors');
    },
    onError: (e) => {
      setShowSuspendConfirm(false);
      setFormError(serverMsg(e) ?? '저장에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const addMoreMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof toPayload>) => createVendor(payload),
    onSuccess: (_, payload) => {
      setSavedSummaries((prev) => [...prev, { vendorName: payload.vendorName, phone: payload.vendorPhone }]);
      setForm(EMPTY_FORM);
      setFormError(null);
      setShowAddConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => {
      setFormError(serverMsg(e) ?? '저장에 실패했습니다. 다시 시도해주세요.');
      setShowAddConfirm(false);
    },
  });

  // 중복이면 저장 불가
  const dupBlocking = dupStatus === 'duplicate';
  const dupMessage = '이미 등록된 사업자번호입니다.';

  async function handleSave() {
    const err = validate(form);
    if (err) { setFormError(err); return; }
    if (dupBlocking) { setFormError(dupMessage); return; }
    setFormError(null);
    // 편집 + 거래정지로 저장 시: 주거래처 지정 여부 확인 후, 지정돼 있으면 확인 모달
    if (isEdit && form.dealStatus === 'SUSPENDED') {
      setSuspendChecking(true);
      try {
        const codes = await fetchOrderSettingVendorWorkTypes(Number(vendorId));
        if (codes.length > 0) {
          setSuspendWorkCodes(codes);
          setShowSuspendConfirm(true);
          return;
        }
      } catch {
        // 조회 실패 시 지정 해제 없이 일반 저장으로 진행
      } finally {
        setSuspendChecking(false);
      }
    }
    saveMutation.mutate({ payload: toPayload(form, isEdit) });
  }

  function handleAddMore() {
    const err = validate(form);
    if (err) { setFormError(err); return; }
    if (dupBlocking) { setFormError(dupMessage); return; }
    setFormError(null);
    setShowAddConfirm(true);
  }

  // 거래품목 공종 옵션 (MultiSelect 용)
  const workTypeOptions = workTypes.map((w) => ({ value: w.workTypeCode, label: w.workTypeName }));

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-8">

      {/* 페이지 타이틀 */}
      <div>
        <h1 className="text-lg font-extrabold tracking-tight text-text">
          {isEdit ? '자재파트너 수정' : '자재파트너 등록'}
        </h1>
        <p className="mt-1 text-xs text-text-muted">
          함께 일할 자재업체 또는 거래처를 등록하세요
        </p>
      </div>

      {/* 이미 저장된 거래처 요약 (등록 모드 + 더 추가 사용 시) */}
      {savedSummaries.length > 0 && (
        <div className="space-y-2">
          {savedSummaries.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-xl border border-border bg-surface-raised px-4 py-3"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-group"
                style={{ background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)' }}
              >
                {s.vendorName.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-sm font-semibold text-text">{s.vendorName}</span>
                <span className="ml-2 text-xs text-text-muted">{s.phone}</span>
              </div>
              <Badge status="success">저장됨</Badge>
            </div>
          ))}
        </div>
      )}

      {/* 폼 카드 */}
      <div className="rounded-xl border border-border bg-surface-raised p-7">
        <div className="mb-3.5">
          <p className="text-xs font-bold uppercase tracking-widest text-text-muted">
            기본 정보
          </p>
        </div>

        {/* 1구역: 상호명 · 사업자번호 · 대표자명 */}
        <div className="border-b border-border pb-2">
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">상호명 <span className="text-error">*</span></label>
            <Input
              value={form.vendorName}
              onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))}
              placeholder="예) (주)인디피아 자재"
              className={INPUT_CLS}
            />
          </div>
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">사업자번호 <span className="text-error">*</span></label>
            <div>
              <Input
                value={form.bizNo}
                onChange={(e) => setForm((f) => ({ ...f, bizNo: formatBizNo(e.target.value) }))}
                placeholder="000-00-00000"
                className={INPUT_CLS}
              />
              {dupStatus === 'checking' && <p className="mt-1.5 text-xs text-text-muted">중복 확인 중…</p>}
              {dupStatus === 'ok' && <p className="mt-1.5 text-xs font-semibold text-success">✓ 등록 가능한 사업자번호예요</p>}
              {dupStatus === 'duplicate' && <p className="mt-1.5 text-xs font-semibold text-error">이미 등록된 사업자예요</p>}
            </div>
          </div>
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">대표자명 <span className="text-error">*</span></label>
            <Input
              value={form.repName}
              onChange={(e) => setForm((f) => ({ ...f, repName: e.target.value }))}
              placeholder="대표자명 입력"
              className={INPUT_CLS}
            />
          </div>
        </div>

        {/* 2구역: 담당자명 · 담당자 연락처 · 이메일 */}
        <div className="border-b border-border pb-2">
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">담당자명 <span className="text-error">*</span></label>
            <Input
              value={form.contactName}
              onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
              placeholder="예) 홍길동"
              className={INPUT_CLS}
            />
          </div>
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">담당자 연락처 <span className="text-error">*</span></label>
            <div>
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                placeholder="010-0000-0000"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-text-muted">자재 발주정보를 카톡으로 보내는 번호예요</p>
            </div>
          </div>
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">이메일</label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="예) contact@company.com"
              className={INPUT_CLS}
            />
          </div>
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">거래 상태 <span className="text-error">*</span></label>
            <div>
              <Segmented
                value={form.dealStatus}
                onChange={(v) => setForm((f) => ({ ...f, dealStatus: v }))}
                options={dealStatusOptions}
              />
              <p className="mt-1 text-xs text-text-muted">거래중지면 공사스케줄 자동매핑 대상에서 빠져요</p>
            </div>
          </div>
          {/* 연락 방식 */}
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">연락 방식 <span className="text-error">*</span></label>
            <div>
              <Segmented
                value={form.commYn ? 'Y' : 'N'}
                onChange={(v) => setForm((f) => ({ ...f, commYn: v === 'Y' }))}
                options={COMM_METHOD_OPTIONS}
              />
              <p className="mt-1 text-xs text-text-muted">{COMM_METHOD_HELP_MATERIAL}</p>
            </div>
          </div>
        </div>

        {/* 3구역: 거래품목 · 세부취급품목 */}
        <div className="border-b border-border pb-2">
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">거래 품목 <span className="text-error">*</span></label>
            <div>
              <MultiSelect
                values={form.workCodes}
                onChange={(v) => setForm((f) => ({ ...f, workCodes: v }))}
                options={workTypeOptions}
                searchable
                placeholder="공종 선택 (여러 개)"
              />
              <p className="mt-1 text-xs text-text-muted">공사스케줄에서 이 공종의 자재파트너를 자동으로 매핑해요</p>
              <p className="mt-1 text-xs text-text-muted">주거래처 지정은 스튜디오 설정 › 발주-입고설정에서 해요</p>
            </div>
          </div>
          <div className="py-1.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">세부취급품목</label>
            <div>
              <Input
                value={form.dealItem}
                onChange={(e) => setForm((f) => ({ ...f, dealItem: e.target.value }))}
                placeholder="예) 수입타일, 욕실자재"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-text-muted">참고 정보로 추천 팝업에 함께 보여요</p>
            </div>
          </div>
        </div>

        {/* 4구역: 배송지역 */}
        <div className="border-b border-border pb-2">
          <div className="py-2.5">
            <label className="mb-1.5 block text-xs font-semibold text-text-secondary">배송지역</label>
            <MultiSelect
              values={form.sigunguCodes}
              onChange={(v) => setForm((f) => ({ ...f, sigunguCodes: v }))}
              options={SIGUNGU_OPTIONS}
              searchable
              placeholder="시·구 검색 (예: 강남)"
            />
            <p className="mt-1 text-xs text-text-muted">추천 팝업에 함께 보여요</p>
          </div>
        </div>

        {/* 5구역: 메모 */}
        <div className="border-b border-border pb-2">
          <div className="py-2.5">
            <label className="mb-1.5 block text-xs font-semibold text-text-secondary">메모</label>
            <Textarea
              className="field-auto"
              value={form.memo}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
              placeholder="특이사항이나 메모를 입력하세요"
              rows={4}
            />
            <p className="mt-1 text-xs text-text-muted">첫 줄이 자재파트너 추천 팝업에 보여요</p>
          </div>
        </div>

        {/* 6구역: 등록자 (읽기전용) — 값이 있을 때만 표시 */}
        {editVendor?.createdByName && (
          <div className="py-2.5" style={ROW_GRID}>
            <label className="text-xs font-semibold text-text-secondary">등록자</label>
            <span className="mt-1 text-xs text-text-muted">{editVendor.createdByName}</span>
          </div>
        )}
      </div>

      {/* 에러 메시지 */}
      {formError && (
        <p className="text-xs font-semibold text-error">{formError}</p>
      )}

      {/* 액션 버튼 */}
      <div className="flex gap-2.5">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/vendors')}
          disabled={saveMutation.isPending || addMoreMutation.isPending}
          className="shrink-0"
        >
          취소
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={saveMutation.isPending || suspendChecking || dupBlocking}
          loading={saveMutation.isPending || suspendChecking}
          className="flex-1"
        >
          {saveMutation.isPending ? '저장 중…' : '저장'}
        </Button>
        {!isEdit && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleAddMore}
            disabled={addMoreMutation.isPending || dupBlocking}
            className="flex-1 !border-group !text-group hover:!bg-group hover:!text-group-onPrimary"
          >
            + 거래처 더 추가
          </Button>
        )}
      </div>

      {/* 더 추가 확인 모달 */}
      {showAddConfirm && (
        <AddMoreConfirmModal
          vendorName={form.vendorName}
          onConfirm={() => addMoreMutation.mutate(toPayload(form))}
          onCancel={() => setShowAddConfirm(false)}
          isPending={addMoreMutation.isPending}
        />
      )}

      {/* 거래정지 — 주거래처 지정 해제 확인 모달 */}
      {showSuspendConfirm && (
        <SuspendReleaseConfirmModal
          workNames={suspendWorkCodes.map(
            (c) => workTypes.find((w) => w.workTypeCode === c)?.workTypeName ?? c,
          )}
          onConfirm={() => saveMutation.mutate({ payload: toPayload(form, isEdit), release: true })}
          onCancel={() => setShowSuspendConfirm(false)}
          isPending={saveMutation.isPending}
        />
      )}
    </div>
  );
}
